import Grid from '@mui/material/Grid'

import { useMutation } from '@tanstack/react-query'
import TimelineForm from 'components/UI/TimelineForm'
import BaseLayout from 'layouts/Base/BaseLayout'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { ExpandMore } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Modal,
  Typography,
} from '@mui/material'
import { getInvoiceSettings } from 'api/clientsAPI'
import { suppliersAPI } from 'api/supplier/suppliersAPI'
import { getUser } from 'api/usersAPI'
import * as yup from 'yup'
import { BillingDetails } from './FormParts/BillingDetails'
import { CompanyDetails } from './FormParts/CompanyDetails'
import { SupplierSettings } from './FormParts/SupplierSettingsSimple'

// const EmptyFormComponent: React.FC = () => null

const ukPostcodeRegex =
  /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) [0-9][A-Za-z]{2})$/
const usZipCodeRegex = /^\d{5}(-\d{4})?$/

const CreateSupplierPageNew: React.FC<any> = () => {
  const history = useHistory()
  // const dispatch = useDispatch()
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [companyLocation, setCompanyLocation] = useState<string>('GBR')
  const [formData, setFormData] = useState({})
  const [formBasis, setFormBasis] = useState({})
  const [showError, setShowError] = useState(false)
  const [errorDetail, setErrorDetail] = useState<any>('')

  useEffect(() => {
    const fetchContext = async () => {
      try {
        const { clientId, roles } = await getUser()
        const { invoiceTypeName } = await getInvoiceSettings(clientId)

        setFormBasis({
          ...formBasis,
          invoiceTypeName,
          isJyrneyAdmin: roles.includes('Jyrney_Admin'),
          clientId,
        })
      } catch (err) {
        console.error(err)
      }
    }
    fetchContext()
      .then(() => console.log('>>>>>', formBasis))
      .catch(console.error)
  }, [])

  const contextFields = {
    step: currentStep,
    companyLocation: companyLocation,
  }

  const newSupplierStepFields: string[][] = [
    [
      'registrationNumber',
      'validated',
      'companyName',
      'alias',
      'address.addressLine1',
      'address.addressLine2',
      'address.town',
      'address.county',
      'address.postCode',
      'companyLocation',
      'accountManager',
      'contact.name',
      'contact.email',
      'contact.contactNumber',
    ],
    [
      'sameAsCompanyAddress',
      'billing.entity',
      'billing.address.addressLine1',
      'billing.address.addressLine2',
      'billing.address.county',
      'billing.address.town',
      'billing.address.postCode',
      'billing.billingTermTypeName',
      'billing.billingTermValue',
      'billing.vatNumber',
      'billing.vatRegistered',
      'billing.payment.accountHolderName',
      'billing.payment.accountNumber',
      'billing.payment.sortCode',
      'billing.contact.name',
      'billing.contact.email',
      'billing.contact.contactNumber',
    ],
    [
      'settings.isOnDemandPricing',
      'settings.isSelfInvoicing',
      'settings.isDelayedInvoicing',
      'services',
      'regionName',
      'termsAndConditions',
    ],
  ]

  const newSupplierSchema = yup.object().shape({
    companyName: yup.string().when('$step', {
      is: 0,
      then: (s) => s.required('Company name is required'),
      otherwise: (s) => s.notRequired(),
    }),
    companyLocation: yup
      .string()
      .min(1, 'Country is required')
      .when('$step', {
        is: 0,
        then: (s) => s.required('Country is required'),
        otherwise: (s) => s.notRequired(),
      }),
    contact: yup.object().shape({
      name: yup.string().when('$step', {
        is: 0,
        then: (s) => s.required('Company Contact Name is required'),
        otherwise: (s) => s.notRequired(),
      }),
      email: yup
        .string()
        .matches(
          /^[\w-.]+@([\w-]+.)+[\w-]{2,4}$/,
          'Enter a valid email address',
        )
        .when('$step', {
          is: 0,
          then: (s) => s.required('Company Contact Email Address is required'),
          otherwise: (s) => s.notRequired(),
        }),
      contactNumber: yup
        .string()
        .when('$step', {
          is: 0,
          then: (s) => s.required('Company Contact Phone Number is required'),
          otherwise: (s) => s.notRequired(),
        })
        .matches(/^\+[1-9]\d{1,14}$/, 'Please enter a valid phone number'),
    }),
    address: yup
      .object()
      .shape({
        addressLine1: yup.string().when('$step', {
          is: 0,
          then: (s) => s.required('Street Address is required'),
          otherwise: (s) => s.notRequired(),
        }),
        town: yup.string().when('$step', {
          is: 0,
          then: (s) => s.required('Town / City is required'),
          otherwise: (s) => s.notRequired(),
        }),
        county: yup.string().when('$step', {
          is: 0,
          then: (s) => s.required('County is required'),
          otherwise: (s) => s.notRequired(),
        }),
        postCode: yup.string().when(['countryCode', '$step'], {
          is: (countryCode: string, step: number) =>
            countryCode === 'GBR' && step === 0,
          then: (s) =>
            s
              .matches(ukPostcodeRegex, 'Invalid UK postcode format')
              .required('Post code is required'),
          otherwise: (s) =>
            s.when(['countryCode', '$step'], {
              is: (countryCode: string, step: number) =>
                countryCode === 'USA' && step === 0,
              then: (schema) =>
                schema
                  .matches(usZipCodeRegex, 'Invalid US ZIP code format')
                  .required('ZIP code is required'),
              otherwise: (schema) =>
                schema.required('Postal / Zip code is required'),
            }),
        }),
        country: yup.string().when('$step', {
          is: 0,
          then: (s) => s.required('Country is required'),
          otherwise: (s) => s.notRequired(),
        }),
        countryCode: yup.string().when('$step', {
          is: 0,
          then: (s) => s.required('Country is required'),
          otherwise: (s) => s.notRequired(),
        }),
      })
      .required(),
    accountManager: yup
      .number()
      .min(1, 'Please choose an account manager')
      .when('$step', {
        is: 0,
        then: (s) => s.required('Please choose an account manager'),
        otherwise: (s) => s.notRequired(),
      }),
    sameAsCompanyAddress: yup.boolean(),
    billing: yup.object().shape({
      entity: yup.string().when('$step', {
        is: 1,
        then: (s) => s.required('Billing Entity is required'),
        otherwise: (s) => s.notRequired(),
      }),
      contact: yup.object().shape({
        name: yup.string().when('$step', {
          is: 1,
          then: (s) => s.required('Billing Contact Name is required'),
          otherwise: (s) => s.notRequired(),
        }),
        email: yup
          .string()
          .matches(
            /^[\w-.]+@([\w-]+.)+[\w-]{2,4}$/,
            'Enter a valid email address',
          )
          .when('$step', {
            is: 1,
            then: (s) =>
              s.required('Billing Contact Email Address is required'),
            otherwise: (s) => s.notRequired(),
          }),
        contactNumber: yup
          .string()
          .matches(/^\+[1-9]\d{1,14}$/, 'Please enter a valid phone number')
          .when('$step', {
            is: 1,
            then: (s) => s.required('Billing Contact Phone Number is required'),
            otherwise: (s) => s.notRequired(),
          }),
      }),
      address: yup.object().shape({
        addressLine1: yup.string().when('$step', {
          is: 1,
          then: (s) => s.required('Street Address is required'),
          otherwise: (s) => s.notRequired(),
        }),
        county: yup.string().when('$step', {
          is: 1,
          then: (s) => s.required('County is required'),
          otherwise: (s) => s.notRequired(),
        }),
        town: yup.string().when('$step', {
          is: 1,
          then: (s) => s.required('Town / City is required'),
          otherwise: (s) => s.notRequired(),
        }),
        postCode: yup.string().when(['countryCode', '$step'], {
          is: (countryCode: string, step: number) =>
            countryCode === 'GBR' && step === 1,
          then: (s) =>
            s
              .matches(ukPostcodeRegex, 'Invalid UK postcode format')
              .required('Post code is required'),
          otherwise: (s) =>
            s.when(['countryCode', '$step'], {
              is: (countryCode: string, step: number) =>
                countryCode === 'USA' && step === 1,
              then: (schema) =>
                schema
                  .matches(usZipCodeRegex, 'Invalid US ZIP code format')
                  .required('ZIP code is required'),
              otherwise: (schema) =>
                schema.required('Postal / Zip code is required'),
            }),
        }),
        country: yup.string().when('$step', {
          is: 0,
          then: (s) => s.required('Country is required'),
          otherwise: (s) => s.notRequired(),
        }),
        countryCode: yup.string().when('$step', {
          is: 1,
          then: (s) => s.required('Country is required'),
          otherwise: (s) => s.notRequired(),
        }),
      }),
      billingTermTypeName: yup.string().when('$step', {
        is: 1,
        then: (s) => s.required('Choose a term'),
        otherwise: (s) => s.notRequired(),
      }),
      billingTermValue: yup
        .number()
        .typeError('Billing term is invalid')
        .min(1, 'Minimum billing term is 7 days')
        .when(['$step', 'billing.billingTermTypeName'], {
          is: (step: number, type: string) =>
            step === 1 && type === 'DaysFromInvoice',
          then: (s) => s.required('Days are required'),
          otherwise: (s) => s.notRequired(),
        })
        .when(['$step', 'billing.billingTermTypeName'], {
          is: (step: number, type: string) =>
            step === 1 && type === 'EndOfMonth',
          then: (s) => s.required('Days are required'),
          otherwise: (s) => s.notRequired(),
        }),
      vatNumber: yup.string().when(['$step', 'billing.vatRegistered'], {
        is: (step: number, vatRegistered: boolean) =>
          step === 1 && vatRegistered,
        then: (s) => s.required('VAT Number Required'),
        otherwise: (s) => s.notRequired(),
      }),

      payment: yup.object().shape({
        accountHolderName: yup
          .string()
          .when('$step', {
            is: 1,
            then: (s) => s.required('Account Holder or Business Name required'),
            otherwise: (s) => s.notRequired(),
          })
          .required(),
        sortCode: yup.string().when('$step', {
          is: 1,
          then: (s) => s.matches(/^[\d]{6}$/, 'Invalid Sort Code').required(),
          otherwise: (s) => s.notRequired(),
        }),
        accountNumber: yup.string().when('$step', {
          is: 1,
          then: (s) =>
            s.matches(/^[\d]{8}$/, 'Invalid Account Number').required(),
          otherwise: (s) => s.notRequired(),
        }),
      }),
    }),
    services: yup
      .array()
      .min(1, 'At least one service needs to be selected')
      .required(),
    termsAndConditions: yup
      .string()
      .matches(
        /((https?):\/\/)?(www.)?[a-z0-9-]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9-#]+)*\/?(\?[a-zA-Z0-9-_-]+=[a-zA-Z0-9-%-]+&?)?$/,
        'Invalid URL!',
      )
      .required('Please provide a link to terms & conditions'),
  })
  const schemaDescription = newSupplierSchema.describe().fields
  console.log('schemaDescription', schemaDescription)

  const defaultNewSupplierValues = {
    validated: false,
    status: 'Onboarding',
    companyLocation: 'GBR',
    registrationNumber: '',
    companyName: '',
    accountManager: 498,
    address: {
      addressLine1: '',
      addressLine2: '',
      town: '',
      county: '',
      postCode: '',
      country: 'United Kingdom',
      countryCode: 'GBR',
    },
    currency: 'GBP',
    timezone: 'Europe/London',
    billing: {
      entity: '',
      billingTermTypeName: 'DaysFromInvoice',
      billingTermValue: 7,
      vatRegistered: false,
      vatNumber: '',
      address: {
        addressLine1: '',
        addressLine2: '',
        town: '',
        county: '',
        postCode: '',
        country: 'United Kingdom',
        countryCode: 'GBR',
      },
      payment: {
        accountHolderName: '',
        sortCode: '',
        accountNumber: '',
      },
    },
    regionName: 'NorthWest',
    termsAndConditions: 'https://',
    services: [],
    settings: {
      isOnDemandPricing: false,
      isSelfInvoicing: false,
      isDelayedInvoicing: false,
    },
  }

  const formsData = [
    {
      formName: 'Supplier Details',
      expanded: true,
      formObject: CompanyDetails,
      shouldRender: true,
    },
    {
      formName: 'Billing Information',
      expanded: false,
      formObject: BillingDetails,
      shouldRender: true,
    },
    {
      formName: 'Service Settings',
      expanded: false,
      formObject: SupplierSettings,
      shouldRender: true,
    },
  ]

  const submitData = useMutation({
    mutationFn: async (formData: any) => {
      return await suppliersAPI.createSupplier(formData)
    },
    onSuccess: async (data) => {
      if (data.data.id) {
        history.push(`/admin/supplier/${data.data.id}`)
      }
    },
    onError: async (error) => {
      setErrorDetail(JSON.stringify(error, null, 2))
      setShowError(true)
    },
  })

  return (
    <BaseLayout
      pageTitle="Create Supplier"
      pageSubtitle="Enter new Suppiler information"
      mainElement={
        <Grid
          container
          justifyContent="center"
          sx={{ display: 'flex', flexDirection: 'row' }}
        >
          <Grid
            item
            xs={12}
            xl={8}
            sx={{ display: 'flex', flexDirection: 'column' }}
          >
            <TimelineForm
              formParts={formsData}
              submitData={submitData}
              defaultValues={defaultNewSupplierValues}
              formSchema={newSupplierSchema}
              stepFields={newSupplierStepFields}
              setCurrentStep={setCurrentStep}
              currentStep={currentStep}
              companyLocation={companyLocation}
              setCompanyLocation={setCompanyLocation}
              formData={formData}
              setFormData={setFormData}
              contextFields={contextFields}
            />
          </Grid>
          <Modal
            open={showError}
            onClose={() => {
              setShowError(false)
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                maxWidth: '70dvw',
                bgcolor: 'background.paper',
                boxShadow: 24,
                borderRadius: '4px',
                p: 4,
              }}
            >
              <Typography id="error-title" variant="h6" component="h2">
                Sorry, there was an issue
              </Typography>
              <Typography id="error-description" sx={{ mt: 2 }}>
                We were unable to create this supplier for you, due to an
                unexpected error.
              </Typography>
              <Typography id="error-description" sx={{ mt: 2 }}>
                Please get in touch with{' '}
                <span style={{ fontWeight: 500 }}>
                  clientservices@jyrney.com
                </span>{' '}
                to report this issue.
              </Typography>

              {errorDetail.length > 0 && (
                <Accordion sx={{ marginTop: '15px' }}>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls="error-details"
                    id="error-details"
                  >
                    <code style={{ color: 'blue' }}>Error Details</code>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      component="pre"
                      id="error-details"
                      sx={{ mt: 2 }}
                    >
                      <code>{errorDetail}</code>
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          </Modal>
          {/* <Grid
            item
            xs={12}
            xl={3}
            sx={{ display: 'flex', flexDirection: 'column' }}
          >
            <Typography
              sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}
            >
              Supplier Status
              <Chip color="success" label={'Onboarding'} />
            </Typography>
            <pre>
              Location: {companyLocation}
              <br />
              Step: {currentStep}
              <br />
              FormData: {JSON.stringify(formData, null, 2)}
            </pre>
          </Grid> */}
        </Grid>
      }
    />
  )
}

export default CreateSupplierPageNew
